import api from "../api";
import { isUUID } from "../../utilities";
import { mapToClinicalColumnModel, mapToColumnModel } from "./mappers";
import { StatusType, ChannelType, CompanyType, EConsentDetailsModel } from "./types";

interface ClinicalTypeParams {
  type: StatusType;
  channel: ChannelType;
  company: CompanyType;
  isDelegateToMe: boolean;
  from?: string;
  to?: string;
  templateGroup?: string;
}

const ClinicalEconsentDetails = async (params: ClinicalTypeParams & { hn?: string }): Promise<EConsentDetailsModel[]> => {
  const { type } = params;
  if (type === StatusType.UNDEFINED) return [];

  const res = await api.get<EConsentDetailsModel[]>(
    `${process.env.REACT_APP_ECONSENT_SUFFIX}/econsent/details`,
    { params }
  )
  return res.data;
}

const EconsentDetailsByHN = async (hn: string, from: string, to: string): Promise<EConsentDetailsModel[]> => {
  if (!hn) return [];

  const params = { from, to };
  try {
    const response = await api.get<EConsentDetailsModel[]>(`EConsent/details/hn/${hn}`, { params });
    return response.data || [];
  } catch (error) {
    return [];
  }
};

export const fetchClinicalData = async (
  status: StatusType,
  channel: ChannelType,
  company: CompanyType,
  isDelegateToMe: boolean,
  from?: string,
  to?: string,
  templateGroup?: string,
  hn?: string
) => {
  const transactions: Promise<EConsentDetailsModel[]>[] = [];
  const params = { channel, company, isDelegateToMe, from, to, templateGroup, hn };
  let includePartialCompleted = false;

  if (status === StatusType.PARTIAL_COMPLETED) {
    transactions.push(ClinicalEconsentDetails({ type: StatusType.PARTIAL_COMPLETED, ...params }));
  }

  if (status === StatusType.PENDING_SIGNER || status === StatusType.ALL) {
    transactions.push(ClinicalEconsentDetails({ type: StatusType.PENDING_SIGNER, ...params }));
    includePartialCompleted = true;
  }

  if (status === StatusType.COMPLETED || status === StatusType.ALL) {
    transactions.push(ClinicalEconsentDetails({ type: StatusType.COMPLETED, ...params }));
    includePartialCompleted = true;
  }

  if (status === StatusType.CANCELLED || status === StatusType.ALL) {
    transactions.push(ClinicalEconsentDetails({ type: StatusType.CANCELLED, ...params }));
  }

  if (includePartialCompleted) {
    transactions.push(ClinicalEconsentDetails({ type: StatusType.PARTIAL_COMPLETED, ...params }));
  }

  const results = await Promise.all(transactions);
  const all = results.flat();
  const sorted = all.sort((a, b) => {
    const date1: number = Number(new Date(a.createdAt ?? 0));
    const date2: number = Number(new Date(b.createdAt ?? 0));
    return date2 - date1;
  });
  return mapToClinicalColumnModel(sorted);
};

export const updateClinicalUrgentCase = async (params: {
  transactionId: string;
  isUrgentCase: boolean;
}): Promise<boolean> => {
  const { transactionId, isUrgentCase } = params;
  if (!isUUID(transactionId)) return Promise.reject(new Error("transactionId is not a valid UUID"));

  const res = await api.patch<boolean>(
    `${process.env.REACT_APP_ECONSENT_SUFFIX}/econsent/transaction/${transactionId}/urgent-case`,
    { isUrgentCase },
    {
      headers: {
        "Content-Type": "application/json",
        Accept: "text/plain",
      },
    }
  );
  return res.data;
};

export const fetchDataByHN = async (hn : string, from: string, to: string) => {
  const transactions = await EconsentDetailsByHN(hn, from, to);
  const sortedTransactions = [...transactions].sort((a, b) => {
    const date1: number = Number(new Date(a.appointmentDateTime ?? 0));
    const date2: number = Number(new Date(b.appointmentDateTime ?? 0));
    return date2 - date1;
  });

  return mapToColumnModel(sortedTransactions);
}